import { useEffect, useState } from 'react';
import type { Session } from '@supabase/supabase-js';

import { getSession, onAuthStateChange } from '@/lib/auth';
import { getAuthBroadcast } from '@/lib/auth-broadcast';

export type SessionState = {
  /** True until the first getSession() call resolves. */
  loading: boolean;
  session: Session | null;
};

export function useSession(): SessionState {
  const [state, setState] = useState<SessionState>({ loading: true, session: null });

  useEffect(() => {
    let active = true;

    void getSession()
      .then((session) => {
        if (active) setState({ loading: false, session });
      })
      .catch(() => {
        if (active) setState({ loading: false, session: null });
      });

    const subscription = onAuthStateChange((session) => {
      if (!active) return;
      setState({ loading: false, session });

      // UB16 — propagate auth changes to other tabs.
      const bc = getAuthBroadcast();
      if (session) {
        bc.publish({ type: 'signed-in', userId: session.user.id });
      } else {
        bc.publish({ type: 'signed-out' });
      }
    });

    const unsubscribe = getAuthBroadcast().subscribe(() => {
      if (!active) return;
      // Cross-tab event — re-fetch current session so this tab catches up.
      void getSession()
        .then((session) => {
          if (active) setState({ loading: false, session });
        })
        .catch(() => undefined);
    });

    return () => {
      active = false;
      subscription.unsubscribe();
      unsubscribe();
    };
  }, []);

  return state;
}
